import { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import { csvStringToObjects } from '../../../utils/csv';
import { generateStableRowId } from '../utils/rowHelpers';

/**
 * Hook for loading and managing CSV data.
 * This hook manages the core data state that will be provided via CsvEditorContext.
 */
export const useCsvData = (downloadCsvString) => {
  const [csvContent, setCsvContent] = useState('');
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]); // Store original data
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]); // Store original header order
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    setIsLoading(true);
    downloadCsvString()
      .then((csvContent) => {
        try {
          setCsvContent(csvContent);
          const { rows: _rows, headers } = csvStringToObjects(csvContent);
          setOriginalColumnOrder(headers); // Store the original column order
          // Sort rows by dmv_make, then dmv_model, then dmv_finish_level (all ascending)
          const sortedRows = [..._rows].sort((a, b) => {
            // Sort by dmv_make first
            const makeA = (a.dmv_make || '').toLowerCase();
            const makeB = (b.dmv_make || '').toLowerCase();
            if (makeA !== makeB) {
              return makeA.localeCompare(makeB);
            }
            // If dmv_make is equal, sort by dmv_model
            const modelA = (a.dmv_model || '').toLowerCase();
            const modelB = (b.dmv_model || '').toLowerCase();
            if (modelA !== modelB) {
              return modelA.localeCompare(modelB);
            }
            // If both are equal, sort by dmv_finish_level
            const finishA = (a.dmv_finish_level || '').toLowerCase();
            const finishB = (b.dmv_finish_level || '').toLowerCase();
            return finishA.localeCompare(finishB);
          });
          // Add stable IDs for DataGrid based on row data (not index)
          // Handle potential duplicate IDs by tracking occurrences
          const idCounts = new Map();
          const rowsWithIds = sortedRows.map((row) => {
            const baseId = generateStableRowId(row);
            // Track how many times we've seen this ID
            const count = idCounts.get(baseId) || 0;
            idCounts.set(baseId, count + 1);
            // Append index only if there are duplicates
            const finalId =
              count > 0 ? generateStableRowId(row, count) : baseId;
            return {
              ...row,
              id: finalId
            };
          });
          setRows(rowsWithIds);
          // Store original rows for comparison (deep copy)
          setOriginalRows(rowsWithIds.map((row) => ({ ...row })));
        } catch (parseError) {
          console.error('Error parsing CSV:', parseError);
          notify('שגיאה בפענוח הקובץ', { type: 'error' });
        }
      })
      .catch((error) => {
        console.error('Error loading CSV:', error);
        notify('שגיאה בטעינת הקובץ', { type: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [downloadCsvString, notify]);

  // Return state in format suitable for CsvEditorContext
  return {
    csvContent, // Kept for potential future use, but not exposed via context
    rows,
    setRows,
    originalRows,
    setOriginalRows,
    originalColumnOrder,
    isLoading
  };
};
